import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { OwnerShare } from '@app/components/pr-form/pr-input-owner-share/pr-input-owner-share.component';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = {
      ...option,
    };
    let options: AbstractControlOptions = {};

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        borrowerInsuranceContext: [
          entity?.parameters?.borrowerInsuranceContext,
        ],
        loan: this.formBuilder.group({
          label: [entity?.parameters?.loan?.label || null, Validators.required],
          category: [
            entity?.parameters?.loan?.category || null,
            Validators.required,
          ],
          providerId: [
            entity?.parameters?.loan?.providerId || null,
            Validators.required,
          ],
          agenceBancaireId: [
            entity?.parameters?.loan?.agenceBancaireId || null,
            Validators.required,
          ],
          // Détenteurs + part de détention (`share`, fraction 0..1).
          // `ownerIds` est déprécié : on utilise désormais `owners`.
          owners: [
            this.buildInitialOwners(entity),
            Validators.required,
          ],
          signatureDate: [
            entity?.parameters?.loan?.signatureDate || null,
            Validators.required,
          ],
          firstDueDate: [
            entity?.parameters?.loan?.firstDueDate || null,
            entity?.parameters?.borrowerInsuranceContext === 'CHANGE'
              ? Validators.required
              : null,
          ],
          effectiveDate: [
            entity?.parameters?.loan?.effectiveDate || null,
            Validators.required,
          ],
        }),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  /**
   * Valeur initiale du champ `owners` (détenteurs + part de détention).
   * - crédit existant : on reprend `entity.parameters.loan.owners` ;
   * - crédit legacy (anciens `ownerIds`) : répartition égale 1/n ;
   * - sinon : vide (l'utilisateur sélectionne les détenteurs).
   */
  buildInitialOwners(
    entity: PledgeProductRead & { parameters: any },
  ): OwnerShare[] {
    const loan = entity?.parameters?.loan;
    if (loan?.owners?.length) {
      return loan.owners.map((o: any) => ({
        ownerId: o.ownerId,
        share: o.share != null ? o.share : null,
      }));
    }
    if (loan?.ownerIds?.length) {
      const share = Math.round((1 / loan.ownerIds.length) * 100) / 100;
      return loan.ownerIds.map((id: number) => ({ ownerId: id, share }));
    }
    return [];
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }

  extract() {
    return this.form.value.products;
  }
}
